// mammoth and pdfjs-dist are both sizeable and only needed on the two job-posting
// pages, so they're dynamically imported inside the extract functions below rather
// than at module scope -- keeps them out of the main bundle for every other page.

export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

// Lines that visually look like a bullet in the source document (Word/PDF bullet glyphs,
// asterisks, hyphens) get normalized to this app's own "- " bullet convention
// (see src/utils/renderDescription.jsx) so the extracted text renders correctly once saved.
const BULLET_CHAR_RE = /^\s*[•▪●◦‣∙*-]\s+/;

function normalizeBulletLine(line) {
  return BULLET_CHAR_RE.test(line) ? `- ${line.replace(BULLET_CHAR_RE, '')}` : line;
}

async function extractFromDocx(file) {
  const { default: mammoth } = await import('mammoth');
  const arrayBuffer = await file.arrayBuffer();
  const { value: html } = await mammoth.convertToHtml({ arrayBuffer });
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const lines = [];
  for (const node of doc.body.children) {
    if (node.tagName === 'UL' || node.tagName === 'OL') {
      for (const li of node.querySelectorAll('li')) {
        const text = li.textContent.trim();
        if (text) lines.push(`- ${text}`);
      }
    } else {
      const text = node.textContent.trim();
      if (text) lines.push(normalizeBulletLine(text));
    }
  }
  return lines.join('\n');
}

async function extractFromPdf(file) {
  const pdfjsLib = await import('pdfjs-dist');
  pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

  const lines = [];
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const content = await page.getTextContent();

    let currentLine = '';
    let lastY = null;
    for (const item of content.items) {
      const y = item.transform[5];
      if (lastY !== null && Math.abs(y - lastY) > 2) {
        if (currentLine.trim()) lines.push(currentLine.trim());
        currentLine = '';
      }
      currentLine += item.str;
      lastY = y;
    }
    if (currentLine.trim()) lines.push(currentLine.trim());
  }
  return lines.map(normalizeBulletLine).join('\n');
}

// Extracts plain text from a .docx or .pdf File, normalizing obvious bullet formatting
// to this app's "- " convention. Throws a user-facing Error message on any failure
// (unsupported type, corrupt/unparseable file, or no extractable text).
export async function extractDocumentText(file) {
  if (file.size > MAX_DOCUMENT_BYTES) {
    throw new Error('That file is too large — please upload a file under 10MB.');
  }

  const ext = file.name.split('.').pop()?.toLowerCase();
  let text;
  try {
    if (ext === 'docx') {
      text = await extractFromDocx(file);
    } else if (ext === 'pdf') {
      text = await extractFromPdf(file);
    } else {
      throw new Error('Unsupported file type — please upload a .docx or .pdf file.');
    }
  } catch (err) {
    if (err.message.startsWith('Unsupported file type')) throw err;
    throw new Error('Could not read that file — it may be corrupted or in an unexpected format.');
  }

  if (!text || !text.trim()) {
    throw new Error("Couldn't find any text in that file — it may be a scanned image without a text layer.");
  }
  return text;
}
